import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { post, del } from '@/lib/api-client';
import { isNativeApp, nativePush } from '@/features/location/native-bridge';
import { ensureNoticeChannel } from './chat-notices';

/** Where the last registered token is kept, so signing out can withdraw it. */
const STORAGE_KEY = 'hr.push.token';

/**
 * Hands this handset's address to the server, so a message can reach it while
 * the app is closed.
 *
 * Every launch, not once. Google rotates a registration token whenever it
 * likes — a reinstall, a restore onto a new phone, months of disuse — and a
 * device quietly holding a retired one is a device that silently stops
 * receiving anything. Re-registering is a single upsert, so the cost of doing
 * it every time is nothing next to the cost of not noticing.
 *
 * Does nothing in a browser or in a shell built before push (up to v1.5),
 * where the plugin is simply absent.
 */
export function usePushRegistration(): void {
  const navigate = useNavigate();
  const goTo = useRef(navigate);
  goTo.current = navigate;

  useEffect(() => {
    if (!isNativeApp()) return;
    const plugin = nativePush();
    if (!plugin) return;

    let cancelled = false;
    const handles: Array<NativeListenerLike> = [];

    const keep = (handle: NativeListenerLike | Promise<NativeListenerLike>) => {
      void Promise.resolve(handle).then((listener) => {
        if (cancelled) void listener.remove().catch(() => undefined);
        else handles.push(listener);
      });
    };

    keep(
      plugin.addListener('registration', (token) => {
        // Kept so signing out can tell the server to stop sending here; a
        // phone handed to somebody else must not keep buzzing for the last
        // person who used it.
        try {
          localStorage.setItem(STORAGE_KEY, token.value);
        } catch {
          // A browser with storage blocked still registers; it just cannot
          // withdraw the token later, which the server prunes on its own.
        }
        void post('/notifications/devices', { token: token.value, platform: 'android' }).catch(
          () => undefined,
        );
      }),
    );

    keep(
      plugin.addListener('registrationError', () => {
        // Nothing to do and nothing to say: a handset without Play services,
        // or one that cannot reach Google right now, keeps the poll's alerts.
      }),
    );

    keep(
      plugin.addListener('pushNotificationActionPerformed', (action) => {
        void goTo.current(action.notification.data?.link ?? '/chat');
      }),
    );

    void (async () => {
      // The channel first: Android draws a pushed notice through the channel
      // named in the message, and one that does not exist yet is created with
      // default settings — no banner, no vibration — and then frozen that way.
      await ensureNoticeChannel();
      try {
        const state = await plugin.checkPermissions();
        const granted =
          state.receive === 'granted' ? state : await plugin.requestPermissions();
        if (granted.receive === 'granted' && !cancelled) await plugin.register();
      } catch {
        // An older shell, or a refusal. The in-app alerts still work.
      }
    })();

    return () => {
      cancelled = true;
      for (const listener of handles) void listener.remove().catch(() => undefined);
    };
  }, []);
}

/** The shape both Capacitor listener returns share. */
interface NativeListenerLike {
  remove(): Promise<void>;
}

/**
 * Tells the server to stop pushing to this handset.
 *
 * Called on signing out rather than on every unmount: the point is the phone
 * changing hands or accounts, not the app navigating.
 */
export async function forgetThisDevice(): Promise<void> {
  let token: string | null = null;
  try {
    token = localStorage.getItem(STORAGE_KEY);
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    return;
  }
  if (!token) return;
  // A DELETE carrying a body, so the server is told which handset rather than
  // dropping every device this person has ever signed in on.
  await del('/notifications/devices', { data: { token, platform: 'android' } }).catch(
    () => undefined,
  );
}
